import type { AutomationCase, AutomationTask } from "@rutamagica/contracts";
// Adapted from Marmelab Atomic CRM deals/stages.ts getDealsByStage, MIT.
// https://github.com/marmelab/atomic-crm/blob/85af317881c52481902c499e525de98be7a619d6/src/components/atomic-crm/deals/stages.ts
// Preserve all configured stages, make unknown stages visible, and sort copies by priority/due date.
/** Funnel columns: a won sale ("Apartado") moves on to "paid" and "done" with its booking. */
export const commercialStages = [
  "new",
  "conversation",
  "quote",
  "awaiting",
  "won",
  "paid",
  "done",
  "lost",
] as const;
export function getCasesByStage(cases: AutomationCase[]) {
  const result: Record<string, AutomationCase[]> = Object.fromEntries(
    [...commercialStages, "uncategorized"].map((stage) => [stage, []]),
  );
  for (const item of cases) {
    const stage = item.commercial.stage === "won" ? (item.commercial.phase ?? "won") : item.commercial.stage;
    result[
      stage && commercialStages.includes(stage as never)
        ? stage
        : "uncategorized"
    ]!.push(item);
  }
  for (const records of Object.values(result))
    records.sort((a, b) => urgency(a) - urgency(b) || due(a) - due(b));
  return result;
}
const pending = (c: AutomationCase) =>
  c.tasks.filter((t) => t.status === "pending");
const urgency = (c: AutomationCase) =>
  Math.min(4, ...pending(c).map((t) => t.priority));
const due = (c: AutomationCase) =>
  Math.min(
    Infinity,
    ...pending(c).map((t) => (t.dueAt ? Date.parse(t.dueAt) : Infinity)),
  );
const mexicoDay = (date: Date) =>
  new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/Mexico_City",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(date);
// Adapted from Atomic CRM tasks/TasksListByDueDate.tsx + tasksPredicate.ts (same MIT commit).
// Shared queue and Mexico boundaries replace upstream ownership and browser-local date boundaries.
export function groupTasksByDueDate(tasks: AutomationTask[], now = new Date()) {
  const groups: Record<string, AutomationTask[]> = {
    Vencidas: [],
    Hoy: [],
    Próximas: [],
    "Sin fecha": [],
  };
  const today = mexicoDay(now);
  for (const task of tasks) {
    const date = task.status === "snoozed" ? task.snoozedUntil : task.dueAt;
    const day = date ? mexicoDay(new Date(date)) : null;
    groups[
      !day
        ? "Sin fecha"
        : day < today
          ? "Vencidas"
          : day === today
            ? "Hoy"
            : "Próximas"
    ]!.push(task);
  }
  return groups;
}
